/**
 * Sectionly FAQ Components Web Component
 * Handles FAQ interaction and animations
 */

class SectionlyFaqWebComponent extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.getAttribute('section-id');
    this.currentActiveIndex = 1;
    this.isAnimating = false;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.sectionElement = document.getElementById(`section-${this.sectionId}`);
    if (!this.sectionElement) {
      console.warn('Sectionly FAQ: Section element not found');
      return;
    }

    this.titleItems = this.sectionElement.querySelectorAll('.sectionly-faq-components-title-item');
    this.contentItems = this.sectionElement.querySelectorAll('.sectionly-faq-components-content-item');
    
    if (this.titleItems.length === 0 || this.contentItems.length === 0) {
      console.warn('Sectionly FAQ: No FAQ items found');
      return;
    }

    this.bindEvents();
    
    // Set initial state
    this.setActiveItem(1);
  }

  bindEvents() {
    this.titleItems.forEach((item, index) => {
      item.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleTitleClick(index + 1);
      });

      // Add keyboard support
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.handleTitleClick(index + 1);
        }
      });

      // Make items focusable
      item.setAttribute('tabindex', '0');
      item.setAttribute('role', 'button');
      item.setAttribute('aria-expanded', index === 0 ? 'true' : 'false');
    });
  }



  handleTitleClick(clickedIndex) {
    if (this.isAnimating || clickedIndex === this.currentActiveIndex) {
      return;
    }

    this.setActiveItem(clickedIndex);
  }

  setActiveItem(newActiveIndex) {
    if (this.isAnimating) return;
    
    this.isAnimating = true;
    const previousIndex = this.currentActiveIndex;
    this.currentActiveIndex = newActiveIndex;

    // Update title states
    this.updateTitleStates(newActiveIndex);
    
    // Update content with animation
    this.updateContentWithAnimation(previousIndex, newActiveIndex);
  }

  updateTitleStates(activeIndex) {
    this.titleItems.forEach((item, index) => {
      const itemIndex = index + 1;
      
      if (itemIndex === activeIndex) {
        // Activate item
        item.classList.add('active');
        item.setAttribute('aria-expanded', 'true');
      } else {
        // Deactivate item
        item.classList.remove('active');
        item.setAttribute('aria-expanded', 'false');
      }
    });
  }

  updateContentWithAnimation(previousIndex, newActiveIndex) {
    const previousContent = this.getContentByIndex(previousIndex);
    const newContent = this.getContentByIndex(newActiveIndex);

    if (!newContent) {
      this.isAnimating = false;
      return;
    }

    // Start fade out animation for previous content
    if (previousContent && previousContent !== newContent) {
      previousContent.classList.add('fade-out');
      
      setTimeout(() => {
        previousContent.classList.remove('active', 'fade-out');
      }, 200);
    }

    // Start fade in animation for new content
    setTimeout(() => {
      newContent.classList.add('active', 'fade-in');
      
      setTimeout(() => {
        newContent.classList.remove('fade-in');
        this.isAnimating = false;
      }, 400);
    }, previousContent && previousContent !== newContent ? 200 : 0);
  }

  getContentByIndex(index) {
    return this.sectionElement.querySelector(`[data-content-id="${index}"]`);
  }

  // Public method to programmatically set active item
  setActive(index) {
    if (index >= 1 && index <= this.titleItems.length) {
      this.setActiveItem(index);
    }
  }

  // Public method to get current active index
  getActiveIndex() {
    return this.currentActiveIndex;
  }

  // Method to handle theme editor updates
  handleThemeEditorUpdate() {
    // Re-initialize when theme editor updates the section
    setTimeout(() => {
      this.init();
    }, 100);
  }
}

// Register the custom element
if (!customElements.get('sectionly-faq-web-component')) {
  customElements.define('sectionly-faq-web-component', SectionlyFaqWebComponent);
}

// Theme editor support
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', function(event) {
    const sectionId = event.detail.sectionId;
    const webComponent = document.querySelector(`sectionly-faq-web-component[section-id="${sectionId}"]`);
    if (webComponent) {
      webComponent.handleThemeEditorUpdate();
    }
  });

  document.addEventListener('shopify:section:reorder', function(event) {
    const sectionId = event.detail.sectionId;
    const webComponent = document.querySelector(`sectionly-faq-web-component[section-id="${sectionId}"]`);
    if (webComponent) {
      webComponent.handleThemeEditorUpdate();
    }
  });

  document.addEventListener('shopify:block:select', function(event) {
    const blockId = event.detail.blockId;
    const blockElement = document.querySelector(`[data-block-id="${blockId}"]`);
    if (blockElement && blockElement.closest('.sectionly-faq-components-wrapper')) {
      const faqId = blockElement.getAttribute('data-faq-id');
      const sectionElement = blockElement.closest('.sectionly-faq-components-wrapper');
      const sectionId = sectionElement.id.replace('section-', '');
      const webComponent = document.querySelector(`sectionly-faq-web-component[section-id="${sectionId}"]`);
      
      if (webComponent && faqId) {
        webComponent.setActive(parseInt(faqId));
      }
    }
  });
}

// Utility functions for external access
window.SectionlyFaqComponents = {
  // Get FAQ component by section ID
  getComponent: function(sectionId) {
    return document.querySelector(`sectionly-faq-web-component[section-id="${sectionId}"]`);
  },
  
  // Set active FAQ item
  setActive: function(sectionId, index) {
    const component = this.getComponent(sectionId);
    if (component) {
      component.setActive(index);
    }
  },
  
  // Get active FAQ index
  getActive: function(sectionId) {
    const component = this.getComponent(sectionId);
    return component ? component.getActiveIndex() : null;
  }
};

// Initialize components when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeFaqComponents);
} else {
  initializeFaqComponents();
}

function initializeFaqComponents() {
  // Ensure all FAQ components are properly initialized
  const faqComponents = document.querySelectorAll('sectionly-faq-web-component');
  faqComponents.forEach(component => {
    if (!component.initialized) {
      component.initialized = true;
    }
  });
} 